import { useCodeLabel, useTranslation } from '@mypoz/i18n';
import { maskNric } from '@mypoz/shared';
import { Card, StatusBadge } from '@mypoz/ui';

import { CLAIM_TYPE } from '@/features/account/dictionary.js';
import { useAccountProfile } from '@/features/account/index.js';
import { ProfileField } from '@/features/account/components/ProfileField.js';
import { ProfileHeader } from '@/features/account/components/ProfileHeader.js';
import { useWarehouse } from '@/features/warehouses/index.js';
import { SECURITY_CENTER_URL } from '@/lib/legacy-api.js';
import { actionLinkClass } from '@/lib/styles.js';

/**
 * The account profile (MYP-72) — one header card plus one read-only field card, same two-card
 * shape as the mentor mockup.
 *
 * View-only (MYP-72, revised a second time 2026-09-15): a mentor follow-up found the live MyPoz
 * site does let a customer edit their profile, so this screen briefly grew an in-session edit
 * form. The mentor then clarified the actual scope: the "account management" module (this screen
 * plus security/linked-accounts/contacts) was never meant to be built at all (tracked separately
 * as MYP-85), and the one screen kept from it — this one — should be view-only and reached from
 * the header's profile icon, not a sidebar entry. See `navigation.ts` and `Layout.tsx` for the
 * routing/entry-point side of that.
 *
 * customerId, NRIC and wallet currency were already fixed in the old edit form (an account
 * identifier and vendor-derived values, not things a customer would change) — now everything on
 * this screen is, for the same reasons plus the scope decision above.
 *
 * There is no payment-method row: payment is chosen per order, on Step 3 of Create order, so an
 * account-wide value here would only suggest a setting this app does not offer.
 *
 * Wired to `alipay_page2` since MYP-73, and only ever rendered once the account has loaded
 * (`SessionGate`), so there is no loading or failure state to draw here. Name, phone, email and
 * NRIC come from that record too (MYP-105) — by decision, until MYP-75 scopes the API to the
 * signed-in customer, they belong to the account the vendor resolves untokened calls to. The NRIC
 * is shown masked, always.
 *
 * Identity verification (MYP-142) sits next to the ID it is about. Verification itself is done on
 * the old website, so a customer who isn't verified gets the link there — the same one Create order
 * shows — and AUTH (认证) notifications open this page.
 */
export function Account() {
  const { t } = useTranslation();
  const codeLabel = useCodeLabel();
  const { profile } = useAccountProfile();
  const notSet = t('account.profile.notSet');
  // Undefined here is a real answer, not a loading state — see useWarehouse's own doc comment —
  // so the raw id is what's shown rather than nothing, same fallback PackageListRow already uses.
  const warehouse = useWarehouse(profile.defaultWarehouseId);

  return (
    <>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-ink">{t('account.profile.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('account.profile.subtitle')}</p>
      </div>

      <div className="grid gap-5">
        <ProfileHeader profile={profile} />

        <Card className="divide-y divide-line">
          <ProfileField label={t('account.profile.fields.customerId')} value={profile.customerId} />
          <ProfileField label={t('account.profile.fields.phone')} value={profile.phone ?? notSet} />
          <ProfileField label={t('account.profile.fields.email')} value={profile.email ?? notSet} />
          <ProfileField
            label={t('account.profile.fields.nric')}
            value={profile.nric ? maskNric(profile.nric) : notSet}
          />
          <ProfileField
            label={t('account.profile.fields.verification')}
            value={
              profile.verification === 'verified' ? (
                <StatusBadge tone="success">{t('account.verification.verified')}</StatusBadge>
              ) : (
                <span className="flex flex-wrap items-center justify-end gap-x-3">
                  <StatusBadge tone="warning">{t('account.verification.notVerified')}</StatusBadge>
                  <a href={SECURITY_CENTER_URL} className={actionLinkClass()}>
                    {t('account.verification.verifyOnMypoz')}
                  </a>
                </span>
              )
            }
          />
          {profile.taobaoAccount ? (
            <ProfileField
              label={t('account.profile.fields.taobaoAccount')}
              value={profile.taobaoAccount}
            />
          ) : null}
          <ProfileField
            label={t('account.profile.fields.walletCurrency')}
            value={profile.walletCurrency}
          />
          <ProfileField
            label={t('account.profile.fields.claimType')}
            value={codeLabel(CLAIM_TYPE, profile.claimType)}
          />
          <ProfileField
            label={t('account.profile.fields.autoInbound')}
            value={t(`account.profile.autoInboundValue.${profile.autoInbound ? 'on' : 'off'}`)}
          />
          <ProfileField
            label={t('account.profile.fields.defaultWarehouse')}
            value={
              profile.defaultWarehouseId
                ? (warehouse?.label ?? profile.defaultWarehouseId)
                : t('account.profile.notSet')
            }
          />
        </Card>
      </div>
    </>
  );
}
